import { useEffect, useRef, useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_TICKERS = [
  { symbol: 'BTC', price: 70248.00, change: 2.4 },
  { symbol: 'ETH', price: 2065.80, change: 1.8 },
  { symbol: 'GOLD', price: 5078.25, change: 0.5 },
  { symbol: 'NVDA', price: 183.14, change: 3.2 },
  { symbol: 'AAPL', price: 255.78, change: -0.3 },
  { symbol: 'GOOGL', price: 303.45, change: 1.1 },
  { symbol: 'MSFT', price: 420.30, change: 0.8 },
];

const formatPrice = (price) => {
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const TickerItem = ({ symbol, price, change }) => (
  <span className="ticker-item" data-testid={`ticker-item-${symbol.toLowerCase()}`}>
    <span className="ticker-symbol">{symbol}</span>
    <span className="ticker-price">${formatPrice(price)}</span>
    <span className={`ticker-change ${change >= 0 ? 'positive' : 'negative'}`}>
      {change >= 0 ? '+' : ''}{change.toFixed(1)}%
    </span>
  </span>
);

export const TickerTape = () => {
  const [tickers, setTickers] = useState(FALLBACK_TICKERS);

  const fetchPrices = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ticker`);
      const data = await res.json();
      if (data.tickers && data.tickers.length > 0) {
        setTickers(data.tickers);
      }
    } catch (e) {
      // keep fallback data
    }
  }, []);

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, [fetchPrices]);

  const items = [...tickers, ...tickers, ...tickers];

  return (
    <div className="ticker-wrapper" data-testid="ticker-tape">
      <div className="ticker-track">
        {items.map((t, i) => (
          <TickerItem key={i} symbol={t.symbol} price={t.price} change={t.change} />
        ))}
      </div>
    </div>
  );
};

export const AdvancedChart = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    container.innerHTML = '';
    
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: "BITSTAMP:BTCUSD",
      interval: "D",
      timezone: "Etc/UTC",
      theme: "dark",
      style: "1",
      locale: "en",
      hide_side_toolbar: true,
      allow_symbol_change: true,
      calendar: false,
      hide_volume: false,
      support_host: "https://www.tradingview.com"
    });
    
    container.appendChild(script);
  }, []);

  return (
    <div className="tradingview-widget-container advanced-chart" ref={containerRef} data-testid="advanced-chart">
      <div className="tradingview-widget-container__widget"></div>
    </div>
  );
};

export const SymbolOverview = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    container.innerHTML = '';
    
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-symbol-overview.js';
    script.async = true;
    script.innerHTML = JSON.stringify({
      symbols: [
        ["Bitcoin", "BITSTAMP:BTCUSD|1D"],
        ["Ethereum", "BITSTAMP:ETHUSD|1D"],
        ["Gold", "OANDA:XAUUSD|1D"],
        ["Apple", "NASDAQ:AAPL|1D"],
        ["Microsoft", "NASDAQ:MSFT|1D"],
        ["NVIDIA", "NASDAQ:NVDA|1D"],
        ["Bank of America", "NYSE:BAC|1D"],
        ["Broadcom", "NASDAQ:AVGO|1D"]
      ],
      chartOnly: false,
      width: "100%",
      height: "100%",
      locale: "en",
      colorTheme: "dark",
      autosize: true,
      showVolume: true,
      showMA: false,
      hideDateRanges: false,
      hideMarketStatus: false,
      hideSymbolLogo: false,
      scalePosition: "right",
      scaleMode: "Normal",
      fontFamily: "-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif",
      fontSize: "10",
      noTimeScale: false,
      valuesTracking: "1",
      changeMode: "price-and-percent",
      chartType: "area",
      lineWidth: 2,
      lineType: 0,
      dateRanges: ["1d|1", "1m|30", "3m|60", "12m|1D", "60m|1W", "all|1M"]
    });
    
    container.appendChild(script);
  }, []);

  return (
    <div className="tradingview-widget-container symbol-overview" ref={containerRef} data-testid="symbol-overview">
      <div className="tradingview-widget-container__widget"></div>
    </div>
  );
};
